// Editor de las features del plan (`plans.features`, migration 0006).
//
// Son los bullets que el usuario final ve en la pantalla de planes y en la de
// pago del login-portal. El backend las acepta desde siempre (FeatureSchema en
// admin-plans-create y admin-plan-version-publish); lo que faltaba era esto.
//
// SHAPE: el backend acepta `string` o `{ label, included }`. Internamente
// trabajamos siempre con la forma completa y al serializar colapsamos a string
// las incluidas — asi un plan que nunca uso el flag `included` sigue guardando
// exactamente lo que guardaba antes, y el diff del historial no se ensucia con
// un cambio de forma que no cambia nada.
//
// `included: false` se renderiza tachado y con ✕ en el login-portal: sirve para
// contrastar contra el plan de arriba ("4K: no").

import { useId } from 'react';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import type { PlanFeature } from '@/lib/api';
import { fieldCls, checkboxCls } from '@/lib/form-styles';

export interface FeatureRow {
  label: string;
  included: boolean;
}

export function toRows(features: PlanFeature[] | undefined): FeatureRow[] {
  return (features ?? []).map((f) =>
    typeof f === 'string'
      ? { label: f, included: true }
      : { label: f.label, included: f.included !== false },
  );
}

export function fromRows(rows: FeatureRow[]): PlanFeature[] {
  return rows
    // Una fila vacia es ruido de edicion, no una feature: se descarta al guardar.
    .filter((r) => r.label.trim() !== '')
    .map((r) => (r.included ? r.label.trim() : { label: r.label.trim(), included: false }));
}

/** Texto corto para el diff de "Cambios" del dialogo de publicacion. */
export function describeFeatures(features: PlanFeature[]): string {
  if (features.length === 0) return 'sin features';
  return features
    .map((f) => (typeof f === 'string' ? f : `${f.label} (no incluida)`))
    .join(' · ');
}

// El backend topea en 20 (FeatureSchema .max(20)). Cortar aca evita que el
// admin escriba la 21 y se entere recien al guardar.
const MAX_FEATURES = 20;

export default function PlanFeaturesEditor({
  rows,
  onChange,
  disabled = false,
}: {
  rows: FeatureRow[];
  onChange: (rows: FeatureRow[]) => void;
  disabled?: boolean;
}): JSX.Element {
  const groupId = useId();

  const update = (i: number, patch: Partial<FeatureRow>): void => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const remove = (i: number): void => onChange(rows.filter((_, idx) => idx !== i));
  const move = (i: number, delta: number): void => {
    const j = i + delta;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    const [row] = next.splice(i, 1);
    if (row) next.splice(j, 0, row);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-ink-muted">
          Features (bullets que ve el usuario)
        </span>
        <span className="text-xs text-ink-muted">
          {rows.length}/{MAX_FEATURES}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-ink-muted">
          Sin features. El plan se muestra igual, solo que sin lista de bullets.
        </p>
      ) : null}

      <ul className="space-y-2">
        {rows.map((row, i) => (
          <li key={i} className="flex items-center gap-2">
            {/* Reordenar con botones y no drag&drop: el orden es el orden en
                que se pintan los bullets, y con teclado tiene que ser posible. */}
            <div className="flex flex-col">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={disabled || i === 0}
                aria-label={`Subir "${row.label || 'feature sin texto'}"`}
                className="px-1 text-xs leading-none text-ink-muted hover:text-ink dark:hover:text-ink-inverted disabled:opacity-30"
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={disabled || i === rows.length - 1}
                aria-label={`Bajar "${row.label || 'feature sin texto'}"`}
                className="px-1 text-xs leading-none text-ink-muted hover:text-ink dark:hover:text-ink-inverted disabled:opacity-30"
              >
                ▼
              </button>
            </div>
            <GripVertical aria-hidden className="h-4 w-4 shrink-0 text-ink-muted/50" />

            <input
              value={row.label}
              onChange={(e) => update(i, { label: e.target.value })}
              disabled={disabled}
              maxLength={120}
              placeholder="Ej: Calidad 4K"
              className={fieldCls({
                block: false,
                extra: `flex-1 ${row.included ? '' : 'line-through text-ink-muted'}`,
              })}
            />

            <label className="flex shrink-0 items-center gap-1.5 text-xs text-ink-muted">
              <input
                type="checkbox"
                name={`${groupId}-included-${i}`}
                checked={row.included}
                onChange={(e) => update(i, { included: e.target.checked })}
                disabled={disabled}
                className={checkboxCls()}
              />
              Incluida
            </label>

            <button
              type="button"
              onClick={() => remove(i)}
              disabled={disabled}
              aria-label={`Eliminar "${row.label || 'feature sin texto'}"`}
              className="shrink-0 rounded p-1.5 text-status-error hover:bg-status-error/10 disabled:opacity-50"
            >
              <Trash2 aria-hidden className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => onChange([...rows, { label: '', included: true }])}
        disabled={disabled || rows.length >= MAX_FEATURES}
        className="inline-flex min-h-[2.25rem] items-center gap-1.5 rounded border border-surface-border dark:border-surface-dark-border px-3 text-sm text-ink dark:text-ink-inverted disabled:opacity-50"
      >
        <Plus aria-hidden className="h-4 w-4" />
        Agregar feature
      </button>
      <p className="text-xs text-ink-muted">
        Destildá &quot;Incluida&quot; para mostrarla tachada — sirve para contrastar contra un
        plan superior.
      </p>
    </div>
  );
}
